import { boundingBox } from '../../common/pdfReport';
import { classificationTable, periodOf, rateTable, transectTable, transectTableNote } from './coastlinePdfReport';

/**
 * The sections a transect-based analysis reports, assembled.
 * Shoreline and Shore-R measure the same thing by the same method - the difference between them is
 * which imagery they draw on, not what a transect means - so one builder serves both and each
 * passes its own title and whatever extra sections it has. CSqueeze and EUSCoast reuse the area
 * section from here and bring their own results.
 * @module TRANSECT_REPORT_SECTIONS
 */

/**
 * @function areaSection
 * @description Where the analysis was: the area file, its extent, and the baseline it measured
 * from.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring.
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {String} [params.kmlFileName] - The uploaded area file.
 * @param {Object} params.text - Translated wording.
 * @memberof module:TRANSECT_REPORT_SECTIONS
 * @returns {Object} A section for {@link renderReport}.
 */
export const areaSection = ({ coordinates, baselineCoords, kmlFileName, text }) => ({
  heading: text.areaHeading,
  rows: [
    [text.areaFile, kmlFileName],
    [text.areaExtent, boundingBox(coordinates)],
    [text.baselineVertices, baselineCoords?.length],
    [text.baselineExtent, boundingBox(baselineCoords)],
  ],
});

/**
 * @function imagerySection
 * @description Which imagery went in: the satellite, how many images, and the dates.
 * @param {Object} params
 * @param {Array<Object>} [params.images] - The chosen images, each with a `date` or an `id`.
 * @param {String} [params.satellite] - The satellite's name.
 * @param {Object} params.text - Translated wording.
 * @memberof module:TRANSECT_REPORT_SECTIONS
 * @returns {Object} A section for {@link renderReport}.
 */
export const imagerySection = ({ images, satellite, text }) => {
  const dates = (images || [])
    .map((image) => image?.date ?? image?.id ?? null)
    .filter(Boolean)
    .map((value) => String(value).slice(0, 10));

  return {
    heading: text.imageryHeading,
    rows: [
      [text.satellite, satellite],
      [text.imageCount, images?.length || null],
      [text.imageList, dates.length ? dates.join(', ') : null],
    ],
  };
};

/**
 * @function transectSections
 * @description The measured result: the rates summarised, how the transects were classified, and
 * the transects themselves.
 * The summary comes before the listing because it is the part that answers the question the
 * analysis was run to answer; the listing is there to be checked against, not read through.
 * @param {Object} params
 * @param {Array<Object>} params.transects - The analysed transects.
 * @param {Object} params.text - Translated wording.
 * @param {Function} [params.className] - Translates a stored class into what the reader sees.
 * @memberof module:TRANSECT_REPORT_SECTIONS
 * @returns {Array<Object>} Sections for {@link renderReport}.
 */
export const transectSections = ({ transects, text, className }) => [
  {
    heading: text.resultHeading,
    rows: [
      [text.transectCount, transects?.length || null],
      [text.measuredPeriod, periodOf(transects)],
    ],
    table: rateTable(transects, text.rates),
    note: transects?.length ? text.resultNote : null,
  },
  {
    heading: text.classHeading,
    table: classificationTable(transects, 'label', text.classes, className),
    note: transects?.length ? text.classNote : null,
  },
  {
    heading: text.transectHeading,
    table: transectTable(transects, text.transects, className),
    note: transectTableNote(transects, text.transectNote),
  },
];

/**
 * @function mapsSection
 * @description The images, when the page had any to offer. A module with no thumbnail passes an
 * empty list and the section is skipped.
 * @param {Object} params
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Translated wording.
 * @memberof module:TRANSECT_REPORT_SECTIONS
 * @returns {Object} A section for {@link renderReport}.
 */
export const mapsSection = ({ images, text }) => ({
  heading: text.mapsHeading,
  images: images || [],
});
